/**
 * ONB-P3: Application status page — progress stepper + status-dependent CTA.
 *
 * Props:
 *   application — current application object
 *   onResume    — () => void — resume editing (pending_details)
 *   onUpload    — () => void — go to verification (pending_verification)
 *   onDashboard — () => void — go to persona owner dashboard (active)
 *   onReapply   — () => void — start new application (rejected)
 */
export function ApplicationStatus({ application, onResume, onUpload, onDashboard, onReapply }) {
  if (!application) {
    return (
      <div style={{ maxWidth: 640, padding: "24px 28px", textAlign: "center" }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", fontFamily: "Georgia, serif" }}>
          No Application Found
        </h1>
        <p style={{ color: "#94a3b8", marginTop: 12 }}>
          You haven't started a claim yet. Start by providing details about the elected official you represent.
        </p>
      </div>
    );
  }

  const status = application.status;

  const STEPS = [
    { key: "pending_details", label: "Details", icon: "📋" },
    { key: "pending_verification", label: "Verification", icon: "🪪" },
    { key: "pending_approval", label: "Review", icon: "🔍" },
    { key: "active", label: "Active", icon: "✅" },
  ];

  const currentIdx = STEPS.findIndex(s => s.key === status);
  const isRejected = status === "rejected";

  return (
    <div style={{ maxWidth: 640, padding: "24px 28px" }}>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", fontFamily: "Georgia, serif", marginBottom: 8 }}>
        Application Status
      </h1>

      {application.official_name && (
        <p style={{ color: "#94a3b8", fontSize: 14, marginBottom: 24 }}>
          Claim for <strong style={{ color: "#e2e8f0" }}>{application.official_name}</strong>
          {application.electoral_position && ` — ${application.electoral_position}`}
        </p>
      )}

      {/* Progress stepper */}
      {!isRejected && (
        <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 32 }}>
          {STEPS.map((step, i) => {
            const done = i < currentIdx;
            const current = i === currentIdx;
            const dotColor = done ? "#16a34a" : current ? "#3b82f6" : "#334155";
            const lineColor = i < currentIdx ? "#16a34a" : "#334155";

            return (
              <div key={step.key} style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? 1 : 0 }}>
                <div style={{ textAlign: "center", minWidth: 60 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%",
                    background: dotColor, display: "flex", alignItems: "center", justifyContent: "center",
                    margin: "0 auto 6px", fontSize: 16,
                    border: current ? "2px solid #3b82f6" : "2px solid transparent",
                  }}>
                    {done ? "✓" : step.icon}
                  </div>
                  <div style={{ fontSize: 10, color: done ? "#16a34a" : current ? "#3b82f6" : "#64748b", fontWeight: current ? 700 : 400 }}>
                    {step.label}
                  </div>
                </div>
                {i < STEPS.length - 1 && (
                  <div style={{ flex: 1, height: 2, background: lineColor, margin: "0 4px", marginBottom: 20 }} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Status-specific content */}
      {status === "pending_details" && (
        <StatusCard
          title="Complete Your Details"
          description="You've started your claim. Fill in the elected official's information to proceed."
          cta="Continue Editing"
          ctaColor="#3b82f6"
          onAction={onResume}
        />
      )}

      {status === "pending_verification" && (
        <StatusCard
          title="Upload Verification Documents"
          description="Your details have been submitted. Upload your government ID and authorisation letter to proceed."
          cta="Upload Documents"
          ctaColor="#3b82f6"
          onAction={onUpload}
        />
      )}

      {status === "pending_approval" && (
        <StatusCard
          title="Under Review"
          description="Your application is being reviewed by our team. We'll notify you by email once a decision is made. This usually takes 1-3 business days."
          icon="⏳"
        />
      )}

      {status === "active" && (
        <StatusCard
          title="Approved!"
          description="Your application has been approved. You can now manage your persona's knowledge base, tone, and credits."
          cta="Go to Dashboard"
          ctaColor="#16a34a"
          onAction={onDashboard}
          icon="🎉"
        />
      )}

      {isRejected && (
        <div>
          <StatusCard
            title="Application Not Approved"
            description="Unfortunately, your application was not approved at this time."
            icon="❌"
          />
          {application.rejection_reason && (
            <div style={{
              padding: "14px 18px", marginTop: 16, borderRadius: 10,
              background: "#ef444415", border: "1px solid #ef444430",
            }}>
              <div style={{ color: "#ef4444", fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Reason</div>
              <div style={{ color: "#fca5a5", fontSize: 13, lineHeight: 1.6 }}>{application.rejection_reason}</div>
            </div>
          )}
          <button
            onClick={onReapply}
            style={{
              marginTop: 20, padding: "12px 28px", borderRadius: 8, border: "none",
              background: "#3b82f6", color: "#fff", fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit", width: "100%",
            }}
          >
            Re-apply
          </button>
        </div>
      )}

      {/* Application metadata */}
      <div style={{ marginTop: 32, padding: "12px 16px", background: "#0d1117", borderRadius: 10, border: "1px solid #1e2d45" }}>
        <div style={{ fontSize: 11, color: "#64748b" }}>
          Application ID: {application.id?.slice(0, 8)}...
          {" · "}
          Created: {new Date(application.created_at).toLocaleDateString()}
          {application.reviewed_at && (
            <> {" · "} Reviewed: {new Date(application.reviewed_at).toLocaleDateString()}</>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusCard({ title, description, cta, ctaColor, onAction, icon }) {
  return (
    <div style={{
      padding: "24px", borderRadius: 12, background: "#0d1117",
      border: "1px solid #1e2d45", textAlign: "center",
    }}>
      {icon && <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>}
      <h2 style={{ color: "#e2e8f0", fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{title}</h2>
      <p style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.6, marginBottom: cta ? 20 : 0 }}>{description}</p>
      {cta && onAction && (
        <button
          onClick={onAction}
          style={{
            padding: "12px 32px", borderRadius: 8, border: "none",
            background: ctaColor, color: "#fff", fontSize: 14, fontWeight: 600,
            cursor: "pointer", fontFamily: "inherit",
          }}
        >
          {cta}
        </button>
      )}
    </div>
  );
}
